import { ConfiguredCarCarCode } from '@oneaudi/onegraph-client';
import { TextButton } from '@oneaudi/unified-web-components';
import { VehicleType as VehicleTypeEcommerce } from '@volkswagen-onehub/e-commerce-service';
import { useAtomValue } from 'jotai/index';
import { Brands, CheckoutType } from 'node_modules/@volkswagen-onehub/e-commerce-service/lib/common/type.js';
import { useCallback, useState } from 'react';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { NextBestActionType, useMarketConfig } from '~/app/market-config';
import { Incentive, VehicleType } from '~/graphql/__generated__/graphql';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import {
	useCarlineQuery,
	useConfiguredCarTrackingQuery,
	useModelTechnicalDataQuery,
} from '~/graphql/hooks/use-configured-car-queries';

import { useAppState, useFeatureServices } from '~/app/contexts/app-context';
import { useI18n } from '~/app/i18n/i18n';
import { hyphenateCarlineId } from '~/app/market-config/hyphenate-carline';
import { PoaTrigger } from '~/app/pages/summary/components/next-best-actions/components/poa-trigger';
import { NbaTargetTypes } from '~/app/tracking/feld-m-types';
import { getResolvedUrl } from '~/app/utils/url-builder';
import { useGetAudiCode } from '~/graphql/hooks/use-get-audicode';
import { state } from '~/storage';
import { DATA_TEST_ID } from '~/test/data-test-id';

export const SummaryCtaButton = ({
	variant,
	size,
	isSecondCta,
	trackClickEvent,
	isFullWidth,
}: {
	variant: 'primary' | 'secondary';
	size: 'md' | 'sm';
	isSecondCta: 'true' | 'false';
	trackClickEvent?: (nbaName: NbaTargetTypes, label: string, url?: string) => void;
	isFullWidth?: boolean;
}) => {
	const { language, config, market } = useMarketConfig();
	const { openDialog } = useDialogs();
	const [isLeanCheckoutLoading, setIsLeanCheckoutLoading] = useState(false);
	const { catalog, carIdentifier } = useConfigurator();
	const defaultProduct = useAtomValue(state.defaultFinancialProduct);
	const technicalData = useModelTechnicalDataQuery(carIdentifier);
	const vehicleType = technicalData.data?.configuredCar.carlineStructureCarline?.vehicleType;
	const eCommerceService = useFeatureServices().eCommerceService;
	const culture = `${language}-${market.toUpperCase()}`;
	const selectedProductSettings = useAtomValue(state.fsagFinancialCalculatorSettings);
	const { getAudiCode, isLoadingAudiCode, shouldDisableButton } = useGetAudiCode();
	const { data: car, loading: carIsLoading } = useConfiguredCarTrackingQuery(carIdentifier);
	const carlineData = useCarlineQuery(carIdentifier, config.financingInput);
	const { financingData } = useAppState();

	const { i18n } = useI18n();

	// use either the submitted CTA or the special OneShop one
	const dynamicCta = useCheckForDynamicCta(
		defaultProduct,
		config.financingInput?.product,
		vehicleType,
		config.enableIncentives,
		carlineData.carline?.configuredCar.incentives,
	);

	const summaryFooterCta: NextBestActionType | undefined =
		isSecondCta === 'true'
			? config.secondarySummaryCta
			: config.leanCheckoutEnabled === true
				? dynamicCta || config.summaryFooterCta
				: config.summaryFooterCta;

	const targetTextReaderOnly = summaryFooterCta?.target === '_blank' ? i18n('ngc.a11y.openInNewTab') : '';

	const handleLeanCheckoutClick = useCallback(
		(audiCode: ConfiguredCarCarCode) => {
			const request = {
				Request: {
					'@ID': 'PL',
					'@Domain': financingData?.financing?.meta?.externalRequestOptions['@Domain']
						? financingData?.financing?.meta?.externalRequestOptions['@Domain']
						: config.financeDomain,
					'@Name': 'Defaults',
					'@Culture': culture,
					Vehicle: {
						...financingData?.financing?.meta?.externalRequestOptions?.Vehicle,
					},
					Product: selectedProductSettings || { '@ID': 'PL' },
					Offer: {},
				},
			};
			const checkout = {
				brand: 'Audi' as Brands,
				customerType: '',
				financialProduct: 'ONLINE_LEASING' as const,
				webCalcRequest: request,
				vehicleIdentifier: audiCode.id,
				ecommerceCheckoutType: 'BUILD_TO_ORDER' as CheckoutType,
				vehicleType: 'MARKETING_CODE' as VehicleTypeEcommerce,
			};
			try {
				eCommerceService.startCheckout(checkout);
			} catch (error) {
				// TODO: what should happen if we have an error!
				setIsLeanCheckoutLoading(false);
				console.error('Unexpected error in Online-Leasing: ', error);
				throw new Error(`ERROR: ${error}`);
			}
		},
		[JSON.stringify(car)],
	);

	const handleClick = useCallback(
		(audiCode: ConfiguredCarCarCode) => {
			if (!summaryFooterCta || !audiCode || Object.keys(audiCode).length === 0) return;
			const resolvedUrl = getResolvedUrl(
				summaryFooterCta.url,
				audiCode.id,
				language,
				encodeURIComponent(catalog.carInfo?.carline ?? ''),
				config.carlineId,
				hyphenateCarlineId(config.carlineId),
				catalog.carInfo?.carIdentifier.model.year.toString(),
				config.financingInput?.product ?? defaultProduct,
				encodeURIComponent(
					catalog.trimlines?.flatMap((t) => t.driveTrains)?.find((item) => item.status.selected)?.name ?? '',
				),
			);

			trackClickEvent &&
				trackClickEvent(
					summaryFooterCta.trackingName as NbaTargetTypes,
					summaryFooterCta.labelText,
					resolvedUrl,
				);

			if (summaryFooterCta.trackingName === 'show audicode') {
				openDialog('audiCode');
			} else if (summaryFooterCta.target === '_poa' && summaryFooterCta.botID) {
				PoaTrigger(summaryFooterCta.botID, audiCode);
			} else if (summaryFooterCta.trackingName === 'online leasing') {
				if (summaryFooterCta.url && summaryFooterCta.url.length > 0) {
					window?.open(resolvedUrl, summaryFooterCta.target);
				} else {
					if (config.leanCheckoutEnabled) handleLeanCheckoutClick(audiCode);
				}
			} else {
				window.open(resolvedUrl, summaryFooterCta.target);
			}
		},
		[summaryFooterCta, summaryFooterCta?.labelText, summaryFooterCta?.url, openDialog, handleLeanCheckoutClick],
	);

	return summaryFooterCta ? (
		<TextButton
			id={summaryFooterCta.trackingName}
			aria-label={
				targetTextReaderOnly
					? `${summaryFooterCta.labelText}. ${targetTextReaderOnly}`
					: summaryFooterCta.labelText
			}
			className={`main-cta-button ${variant}`}
			isLoading={!car || isLeanCheckoutLoading || isLoadingAudiCode || carIsLoading}
			disabled={shouldDisableButton}
			variant={variant}
			onClick={() => getAudiCode(handleClick)}
			size={size}
			data-testid={DATA_TEST_ID.MAIN_CTA_BUTTON}
			isFullWidth={isFullWidth}>
			{summaryFooterCta.labelText}
		</TextButton>
	) : (
		<></>
	);
};

// Tests if oneShop should be activated. If so, it returns a button-configuration - otherwise returns undefined.
const useCheckForDynamicCta = (
	defaultProduct: string | undefined,
	financingInput: string | undefined,
	vehicleType: VehicleType | null | undefined,
	incentivesEnabled: boolean | undefined,
	incentives: Incentive[] | undefined,
): NextBestActionType | undefined => {
	const { i18n } = useI18n();

	if (((defaultProduct === 'PL' && !financingInput) || financingInput === 'PL') && vehicleType === 'BEV') {
		let notApplicable = false;
		if (incentivesEnabled && incentives) {
			incentives.forEach((i) => {
				if (
					i.status.selected &&
					i.customerGroup.classification.length === 1 &&
					i.customerGroup.classification.includes('SMALL_COMMERCIAL')
				) {
					notApplicable = true;
				}
			});
		}

		if (notApplicable) return undefined;

		return {
			labelText: i18n('ngc.actions.onlineLeasing'),
			description: '',
			url: '',
			trackingName: 'online leasing',
			icon: 'shopping-basket',
			target: '',
		};
	}
};
